import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, MessageSquare } from 'lucide-react';
import type { SmsTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useSmsTemplatesList, useSmsTemplateMutations } from '~/hooks/use-sms-templates';

/** SMS/Viber templates CRUD surface — parity with the legacy admin grid (Code/Channel/Body/Visible/Sort). */
export function SmsTemplatesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useSmsTemplatesList();
	const { remove } = useSmsTemplateMutations();

	const [deleteRow, setDeleteRow] = useState<SmsTemplateToken | null>(null);

	const channelLabel = (ch?: number) => (ch === 1 ? t('system.smsTemplates.channelViber') : t('system.smsTemplates.channelSms'));

	const columns = useMemo<SystemGridColumn<SmsTemplateToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.smsTemplates.columns.code'),
				width: '220px',
				sortValue: s => s.cd,
				exportValue: s => s.cd,
				render: s => <span className="truncate font-mono text-xs font-medium text-foreground">{s.cd}</span>
			},
			{
				key: 'channel',
				header: t('system.smsTemplates.columns.channel'),
				width: '90px',
				sortValue: s => s.channel ?? 0,
				exportValue: s => channelLabel(s.channel),
				render: s => <span className="text-sm text-foreground-muted">{channelLabel(s.channel)}</span>
			},
			{
				key: 'body',
				header: t('system.smsTemplates.columns.body'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.bd ?? '',
				exportValue: s => s.bd ?? '',
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.bd ?? ''}>
						{s.bd}
					</span>
				)
			},
			{
				key: 'viberImageUrl',
				header: t('system.smsTemplates.viberImageUrl'),
				width: '200px',
				defaultVisible: false,
				sortValue: s => s.viberImageUrl ?? '',
				exportValue: s => s.viberImageUrl ?? '',
				render: s => <span className="truncate text-xs text-foreground-muted">{s.viberImageUrl || '—'}</span>
			},
			{
				key: 'viberActionUrl',
				header: t('system.smsTemplates.viberActionUrl'),
				width: '200px',
				defaultVisible: false,
				sortValue: s => s.viberActionUrl ?? '',
				exportValue: s => s.viberActionUrl ?? '',
				render: s => <span className="truncate text-xs text-foreground-muted">{s.viberActionUrl || '—'}</span>
			},
			{
				key: 'viberActionText',
				header: t('system.smsTemplates.viberActionText'),
				width: '160px',
				defaultVisible: false,
				sortValue: s => s.viberActionText ?? '',
				exportValue: s => s.viberActionText ?? '',
				render: s => <span className="truncate text-xs text-foreground-muted">{s.viberActionText || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.smsTemplates.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: s => (s.iv ? 1 : 0),
				exportValue: s => (s.iv ? t('common.yes') : t('common.no')),
				render: s => (s.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.smsTemplates.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: s => s.si ?? 0,
				exportValue: s => s.si ?? 0,
				render: s => <span className="text-sm tabular-nums text-foreground-muted">{s.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SmsTemplateToken>
				gridId="sms-templates"
				title={t('system.smsTemplates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.cd}
				columns={columns}
				searchPredicate={(s, q) => `${s.cd ?? ''} ${s.bd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'code', dir: 'asc' }}
				emptyIcon={<MessageSquare size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.smsTemplates.add')}
				onAdd={() => navigate('/system/sms-templates/create')}
				onEdit={s => navigate(`/system/sms-templates/${s.id}`)}
				onDelete={setDeleteRow}
				exportFilename="sms-templates"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.smsTemplates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.smsTemplates.deleteBody', { name: deleteRow?.cd ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
